/**
 * Sonda do 2º decoder de vídeo.
 *
 * O motor agendado (A/B) precisa de DOIS decodificadores de vídeo ao mesmo tempo: o que está
 * tocando e o que já decodificou o próximo quadro pra entrar no instante T. Num aparelho que
 * só entrega um, a preparação falha — hoje em silêncio — e a tela fica preta.
 *
 * **Perguntar ao aparelho não serve.** O Tab A7 DECLARA 16 instâncias simultâneas de AVC em
 * `media_codecs.xml` e, na prática, recusa a 3ª: a declaração é o que o chip comporta no
 * papel, não o que a memória do aparelho entrega. Um número alto não é promessa de nada.
 * Só TENTAR diz a verdade.
 *
 * Por isso o A/B nasce DESLIGADO e é ligado só depois que esta sonda consegue, de fato,
 * decodificar um quadro num segundo elemento. Não conseguiu → o player segue no motor
 * reativo com um decoder, que foi medido tocando normalmente (21 quadros, 19 trocas, zero
 * congelamento, gatilho entrando). O A/B é uma otimização que se conquista, não um padrão
 * a ser desfeito quando quebra.
 *
 * Decisão do Renato (2026-10-12): até 6 tentativas, ~1 por segundo. Uma recusa pontual
 * (outro app segurando o decoder por um instante) não pode condenar o aparelho pra sempre.
 */

/** Tentativas antes de desistir do A/B pela sessão. */
export const SONDA_TENTATIVAS = 6;

/** Intervalo entre tentativas. */
export const SONDA_INTERVALO_MS = 1_000;

/**
 * Quanto esperamos o quadro aparecer numa tentativa. A recusa costuma ser SILENCIOSA — nem
 * `loadeddata` nem `error` chegam —, então sem prazo não existe sinal nenhum.
 *
 * 1.700 e não 1.200: um aparelho apenas LENTO não pode ser confundido com um sem decoder.
 */
export const SONDA_PRAZO_MS = 1_700;

/** `readyState` em que o elemento já decodificou o quadro atual (HAVE_CURRENT_DATA). */
const QUADRO_DECODIFICADO = 2;

/** O pedaço de `HTMLVideoElement` que a sonda toca — o resto não interessa. */
export interface ElementoSondavel {
  src: string;
  preload: string;
  muted: boolean;
  readonly readyState: number;
  load(): void;
  removeAttribute(nome: string): void;
  addEventListener(
    tipo: string,
    ouvinte: () => void,
    opcoes?: { once?: boolean },
  ): void;
  removeEventListener(tipo: string, ouvinte: () => void): void;
}

export interface ParametrosDaSonda {
  elemento: ElementoSondavel;
  /** URL de um vídeo que já está em cache — a sonda mede decoder, não rede. */
  url: string;
  tentativas?: number;
  intervaloMs?: number;
  prazoMs?: number;
  /** Desmontou / mudou de mídia no meio da sonda. */
  cancelado?: () => boolean;
  /** Injetável pra teste não esperar de verdade. */
  esperar?: (ms: number) => Promise<void>;
  aoTentar?: (tentativa: number, total: number) => void;
}

const esperarDeVerdade = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Devolve o decoder: tirar o `src` e recarregar é o que faz o elemento soltar o codec.
 * Só pausar NÃO solta — o decoder continua preso ao elemento.
 */
export function soltarDecoderDoElemento(elemento: ElementoSondavel): void {
  try {
    elemento.removeAttribute('src');
    elemento.load();
  } catch {
    // Elemento já descartado pelo React — nada a devolver.
  }
}

/**
 * Tenta decodificar um quadro num segundo elemento de vídeo.
 *
 * Devolve o decoder ao final de TODA tentativa, inclusive da que deu certo: quem segura o
 * segundo decoder no uso normal é o `prepare()` do motor, 2s antes de cada troca. A sonda
 * só responde "o aparelho deixa?" e sai de cena.
 *
 * @returns `true` se algum quadro foi decodificado — aí o A/B pode ligar.
 */
export async function sondarSegundoDecoder({
  elemento,
  url,
  tentativas = SONDA_TENTATIVAS,
  intervaloMs = SONDA_INTERVALO_MS,
  prazoMs = SONDA_PRAZO_MS,
  cancelado,
  esperar = esperarDeVerdade,
  aoTentar,
}: ParametrosDaSonda): Promise<boolean> {
  const umaTentativa = (): Promise<boolean> =>
    new Promise<boolean>((resolve) => {
      let encerrado = false;
      let prazo: ReturnType<typeof setTimeout> | null = null;

      const terminar = (conseguiu: boolean): void => {
        if (encerrado) return;
        encerrado = true;
        if (prazo) clearTimeout(prazo);
        elemento.removeEventListener('loadeddata', aoCarregar);
        elemento.removeEventListener('error', aoFalhar);
        resolve(conseguiu);
      };
      const aoCarregar = (): void => terminar(true);
      const aoFalhar = (): void => terminar(false);

      elemento.addEventListener('loadeddata', aoCarregar);
      elemento.addEventListener('error', aoFalhar);
      // Silêncio é a resposta mais comum de um aparelho sem decoder sobrando.
      prazo = setTimeout(
        () => terminar(elemento.readyState >= QUADRO_DECODIFICADO),
        prazoMs,
      );

      try {
        elemento.muted = true;
        elemento.preload = 'auto';
        elemento.src = url;
        elemento.load();
      } catch {
        terminar(false);
      }
    });

  for (let tentativa = 1; tentativa <= tentativas; tentativa += 1) {
    if (cancelado?.()) return false;
    aoTentar?.(tentativa, tentativas);

    const conseguiu = await umaTentativa();
    soltarDecoderDoElemento(elemento);
    if (cancelado?.()) return false;
    if (conseguiu) return true;

    if (tentativa < tentativas) await esperar(intervaloMs);
  }

  return false;
}
